import {
  Box,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import { LoanProductBaseFieldConfig } from './LoanProductBaseFieldConfig';
import { LoanProductIntermediateCalcs } from './LoanProductIntermediateCalcs';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import {
  CreditAgreement,
  getIterationKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { LoanProductEntity } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/LoanProductEntity';
import { PropertyTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/PropertyTable';

type LoanProductProps = {
  loanProductMechanics: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
};
type EligibilityTestsTableProps = {
  eligibilityTests: CreditAgreement.DomainEntity.EligibilityTestProperty[];
};

const HEADERS = ['name', 'value', 'section', 'description', 'formula'];

const EligibilityTestsTable = ({
  eligibilityTests,
}: EligibilityTestsTableProps) => {
  return (
    <Flex width="100%" overflowY="auto">
      <Table borderWidth="1px">
        <Thead>
          <Tr>
            {HEADERS.map((header) => (
              <Th key={header} minWidth={header === 'name' ? '25rem' : '10rem'}>
                {_.startCase(header)}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {eligibilityTests.map((eligibilityTest) => {
            return (
              <Tr key={getIterationKey(eligibilityTest)}>
                <Td>{eligibilityTest.name}</Td>
                <Td>{eligibilityTest.value}</Td>
                <Td>{eligibilityTest.section || '-'}</Td>
                <Td>{eligibilityTest.description || '-'}</Td>
                <Td>
                  {eligibilityTest._config_?.eligibilityTestConfig?.type ===
                    'formula' && (
                    <FormulaContainer>
                      {
                        eligibilityTest._config_.eligibilityTestConfig.config
                          .formula
                      }
                    </FormulaContainer>
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Flex>
  );
};

export const LoanProduct = ({ loanProductMechanics }: LoanProductProps) => {
  if (!loanProductMechanics || !loanProductMechanics.entity) {
    return <Skeleton />;
  }

  return (
    <Box mt="20px" width="100%" p="20px" borderWidth="1px">
      <LoanProductEntity
        loanProductEntity={loanProductMechanics.entity}
        config={loanProductMechanics._config_}
      />

      <LoanProductBaseFieldConfig
        baseFields={loanProductMechanics._config_.baseFieldConfigs}
      />
      <LoanProductIntermediateCalcs
        intermediateCalcs={
          loanProductMechanics._config_.intermediateCalcConfigs
        }
      />

      <Text fontSize="20px" fontWeight="bold">
        Receivable Advance Rate
      </Text>
      <PropertyTable
        propertyPairs={[
          ['receivableAdvanceRate', loanProductMechanics.receivableAdvanceRate],
        ]}
        columnWidth={25}
        basePropertyKey=""
        headerNameMapping={{
          value: 'formula',
        }}
      />

      <Text fontSize="20px" fontWeight="bold">
        Receivable Valuation Rate
      </Text>
      <PropertyTable
        propertyPairs={[
          [
            'receivableValuationRate',
            loanProductMechanics.receivableValuationRate,
          ],
        ]}
        columnWidth={25}
        basePropertyKey=""
        headerNameMapping={{
          value: 'formula',
        }}
      />

      <Text fontSize="20px" fontWeight="bold">
        Purchase Percentage Rate
      </Text>
      <PropertyTable
        propertyPairs={[
          [
            'purchasePercentageRate',
            loanProductMechanics.purchasePercentageRate,
          ],
        ]}
        basePropertyKey=""
        columnWidth={25}
        headerNameMapping={{
          value: 'formula',
        }}
      />

      <Text fontSize="20px" fontWeight="bold">
        Concentration Limits
      </Text>
      {_.isEmpty(loanProductMechanics.concentrationLimits) && (
        <Text>No concentration limits have been configured</Text>
      )}
      {!_.isEmpty(loanProductMechanics.concentrationLimits) && (
        <PropertyTable
          propertyPairs={_.toPairs(loanProductMechanics.concentrationLimits)}
          basePropertyKey=""
        />
      )}

      <Text fontSize="20px" fontWeight="bold">
        Eligibility Tests
      </Text>
      {_.isEmpty(loanProductMechanics.eligibilityTests) ? (
        <Text>No eligibility tests have been configured</Text>
      ) : (
        <EligibilityTestsTable
          eligibilityTests={loanProductMechanics.eligibilityTests}
        />
      )}
    </Box>
  );
};
